import type { AxiosRequestConfig } from "axios";
import api from "@/api/client";
import { db } from "@/db";
import {
  getDownloadedPrefixes,
  getGlobalSyncTimestamp,
  setGlobalSyncTimestamp,
} from "./offlineMeta";

/**
 * Requests updated customers/notes/address_states for downloaded prefixes
 * since the last global inbound sync timestamp. Addresses are immutable and
 * are not expected in this payload.
 */
export async function refreshDownloadedPrefixes() {
  const prefixes = await getDownloadedPrefixes();
  if (!prefixes.length) return { refreshed: 0 };

  const since = await getGlobalSyncTimestamp();
  const payload = { prefixes, since };

  const config: AxiosRequestConfig & { suppressGlobalErrorToast?: boolean } = {
    suppressGlobalErrorToast: true,
  };
  const res = await api.post("offline/refresh/", payload, config);
  const data = res.data ?? {};

  const {
    customers = [],
    address_states = [],
    notes = [],
    deleted_notes = [],
    new_sync_timestamp = null,
  } = data;

  await db.transaction("rw", [db.customers, db.addressStates, db.notesSynced, db.meta], async () => {
    if (customers.length) {
      await db.customers.bulkPut(customers);
    }
    if (address_states.length) {
      await db.addressStates.bulkPut(address_states);
    }
    if (deleted_notes.length) {
      const deleteIds = deleted_notes
        .map((d: unknown) => {
          if (Array.isArray(d)) return d[0] as number | null;
          if (typeof d === "number") return d;
          if (typeof d === "string") return Number(d) || null;
          return (d as { id?: number | null })?.id ?? null;
        })
        .filter((id: number | null): id is number => typeof id === "number");
      if (deleteIds.length) {
        await db.notesSynced.bulkDelete(deleteIds);
      }
    }
    if (notes.length) {
      await db.notesSynced.bulkPut(notes);
    }

    if (new_sync_timestamp) {
      await setGlobalSyncTimestamp(new_sync_timestamp);
    }
  });

  return {
    refreshed: prefixes.length,
    customers: customers.length,
    address_states: address_states.length,
    notes: notes.length,
    deleted_notes: deleted_notes.length,
    new_sync_timestamp: new_sync_timestamp ?? null,
  };
}
